import { useEffect, useRef, useState } from 'react';
import { Mui, MuiIcons } from '@osu/react-ui';
import { 
    useGetSystemWideMessagesQuery, 
    useDismissSystemWideMessagesMutation 
} from '../services/graphql/api';
import useRoleAuthentication from '../Authentication/hooks/useRoleAuthentication';

const SystemWideMessageChecker = () => {
    const authDetails = useRoleAuthentication(); 
    const initialApiCallComplete = useRef(false);
    const [dismissedIds, setDismissedIds] = useState(new Set());
    
    // Poll every 10 minutes - skip if not authenticated
    const { data, refetch } = useGetSystemWideMessagesQuery(undefined, {
        skip: !authDetails?.user?.osuid,
    });

    const [dismissMessages, { isLoading: isDismissing }] = useDismissSystemWideMessagesMutation();
       
    // Set up polling interval (only once when user is authenticated)
    useEffect(() => {
        if (initialApiCallComplete.current || !authDetails?.user?.osuid) { 
            return;
        }
        
        initialApiCallComplete.current = true;
        
        const intervalMs = 60 * 10 * 1000; // 10 minutes
        const timer = setInterval(() => {
            if (authDetails?.user?.osuid) {
                refetch();
            }
        }, intervalMs);
        
        return () => clearInterval(timer);
    }, [authDetails?.user?.osuid, refetch]);

    const handleDismiss = async (messageId) => {     
        // Hide this specific message immediately for smooth user experience
        setDismissedIds(prev => new Set([...prev, messageId]));

        // Send dismissal to backend
        try {
            await dismissMessages({ input: { messageIds: [messageId] } }).unwrap();
        } catch (err) {
            console.error('Error dismissing message:', err);
            // On error, restore this message
            setDismissedIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(messageId);
                return newSet;
            });
        }
    };

    // Filter out dismissed messages
    const displayedMessages = (data?.messages || []).filter(msg => !dismissedIds.has(msg.id));

    return (
        <Mui.Box
            sx={{
                position: 'fixed',
                top: '64px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1400,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                maxWidth: '600px',
                width: '100%',
                px: 2,
            }}
        >
            {displayedMessages.map((msg) => (
                <Mui.Snackbar
                    key={msg.id}
                    open={true}
                    sx={{ 
                        position: 'relative',
                        transform: 'none',
                        left: 'auto',
                        right: 'auto',
                        top: 'auto',
                        bottom: 'auto',
                    }}
                >
                    <Mui.Alert
                        severity="warning"
                        sx={{ 
                            width: '100%',
                            fontSize: '1rem',
                            '& .MuiAlert-message': {
                                width: '100%',
                            }
                        }}
                        action={
                            <Mui.IconButton
                                size="small"
                                aria-label="close"
                                color="inherit"
                                onClick={() => handleDismiss(msg.id)}
                                disabled={isDismissing}
                            >
                                <MuiIcons.Close fontSize="small" />
                            </Mui.IconButton>
                        }
                    >
                        {msg.message}
                    </Mui.Alert>
                </Mui.Snackbar>
            ))}
        </Mui.Box>
    );
};

export default SystemWideMessageChecker;


              
